import axios from "axios";
import qs from "qs";
import { getStudents, getTeacherCourse, getToken } from "./service";
import _ from "underscore";
import React from "react";

interface Notice {
    id: number;
    title: string;
    content: string;
    createTime: string;
    updateTime: string;
}

export enum NoticeTargetType {
    Course = "0",
    Student = "1"
}

interface SaveMessageParams {
    id?: string;
    title: string;
    content: string;
    targetType: NoticeTargetType;
    studentNos: string[];
    students: string; // 用来回显公告范围
}

interface NoticeDetail {
    id: number;
    title: string;
    content: string;
    targetType: NoticeTargetType;
    createTime?: string;
    courses:
        | {
              courseNo: string;
              courseSeq: string;
          }[]
        | null;
    studentNos: string | null;
}

interface NoticeCourse {
    courseNo: string;
    courseSeq: string;
}

export interface NoticeListItem {
    id: number;
    title: string; // 标题
    content: string; // 内容
    courses?: NoticeCourse[]; // 内容
    studentNos?: string; // 学生
    students?: string; // 学生姓名
    createUser: string; // 创建的教师
    createTime: string; // 创建时间
    needReadCount: number; // 需要阅读人数
    readCount: number; // 已读人数
}

interface NoticeList {
    model1List: NoticeListItem[];
    count: number;
}

/**
 * @name 获取通知消息
 */
export function getMessageList(pageNo: number = 1, pageSize: number = 20) {
    const token = getToken();
    const params = qs.stringify({ pageNo, pageSize });
    return axios
        .get<SuccessData<NoticeList>>(
            `/teacher/message/getMessageList?${params}`,
            {
                headers: { token }
            }
        )
        .then(res => res.data.data);
}

/**
 * @name 删除通知消息
 * @param id 通知ID
 */
export function deleteMessage(id: number) {
    const token = getToken();
    return axios
        .get<SuccessData<Notice[]>>(`/teacher/message/deleteMessage`, {
            params: { id },
            headers: { token }
        })
        .then(res => res.data.data);
}

/**
 * @name 创建/修改通知消息
 * @param data
 */
export function saveMessage(data: SaveMessageParams) {
    const token = getToken();
    const stdno = data.studentNos.map(item => {
        return item.split("+")[1];
    });
    const studentNos = stdno.join(",");

    return axios
        .post<SuccessData<Notice[]>>(
            `/teacher/message/saveMessage`,
            qs.stringify({ ...data, studentNos }),
            { headers: { token } }
        )
        .then(res => res.data.data);
}

/**
 * @name 获取通知详情
 * @param id 通知ID
 */
export function getMessage(id: string) {
    const token = getToken();
    return axios
        .get<SuccessData<NoticeDetail>>(`/teacher/message/getMessage`, {
            params: { id },
            headers: { token }
        })
        .then(res => res.data.data);
}

/**
 * @name 获取课程和学生
 */
export async function getCourseWithStudent() {
    const courseList = await getTeacherCourse();
    return Promise.all(
        courseList.map(async course => {
            const students = await getStudents(
                course.courseNo,
                course.courseSeq
            );
            return { students, ...course };
        })
    );
}

interface StudentDomain {
    name: "于佳";
    studentno: "yp20200098";
}

export interface NoticeCourseItem {
    courseName: string;
    courseNo: string;
    courseSeq: string;
    studentDomainList: StudentDomain[];
}

/**
 * 发送消息范围
 */
export function getMessageScope() {
    const token = getToken();
    return axios
        .get<SuccessData<NoticeCourseItem[]>>(
            `/teacher/message/getMessageScope`,
            {
                headers: { token }
            }
        )
        .then(res => res.data.data)
        .then(data =>
            data.map(course => ({
                title: `${course.courseName} (${course.courseNo} - ${course.courseSeq})`,
                label: course.courseName,
                value: `${course.courseNo}-${course.courseSeq}`,
                key: `${course.courseNo}-${course.courseSeq}`,
                children: course.studentDomainList.map(stu => ({
                    title: `${stu.name} (${stu.studentno})`,
                    label: `${course.courseName} - ${stu.name}`,
                    value: `${course.courseNo}+${stu.studentno}+${course.courseSeq}`,
                    key: `${course.courseNo}+${stu.studentno}+${course.courseSeq}`
                }))
            }))
        );
}

/**
 * 清空全部
 */
export function clearAll() {
    const token = getToken();
    return axios
        .get<SuccessData<NoticeCourseItem[]>>(`/teacher/message/clearAll`, {
            headers: { token }
        })
        .then(res => res.data.data);
}

export interface StuReadItem {
    studentId: string; // 学号
    studentName: string; // 姓名
}

export interface ReadAndUnread {
    readList?: StuReadItem[];
    unReadList?: StuReadItem[];
}

/**
 * 获取已读/未读列表
 */
export function getReadAndUnread(messageId: number) {
    const token = getToken();
    return axios
        .get<SuccessData<ReadAndUnread>>(
            `/teacher/message/getReadAndUnread?messageId=${messageId}`,
            { headers: { token } }
        )
        .then(res => res.data.data);
}

export interface TreeNode {
    title: string;
    label: React.ReactNode;
    value: string;
    key: string;
    children?: TreeNode[];
}

/**
 * 树形变成一维结构, 方便select显示
 * @param treeData
 */
export function flattenTreeData(treeData: TreeNode[]) {
    const data: { title: string; label: React.ReactNode; value: string }[] = [];

    _.each(treeData, course => {
        data.push({
            title: course.title,
            label: course.label,
            value: course.value
        });

        _.each(course.children || [], stu => {
            if (_.some(data, { value: stu.value })) return; // 防止数据重复
            data.push({ title: stu.title, label: stu.label, value: stu.value });
        });
    });

    return data;
}

/**
 * 把同一班级的学生合并
 * @param treeData
 * @param selected
 */
export function zipSelected(treeData: TreeNode[], selected: string[]) {
    const courses = _.pluck(
        treeData.filter(course => {
            const students = _.pluck(course.children || [], "value");
            return _.every(students, stu => selected.includes(stu));
        }),
        "value"
    );

    const students = otherStudents(treeData, selected, courses);

    return [...courses, ...students];
}

/**
 * 把合并后的数据还原
 * @param treeData
 * @param readableSelected
 */
export function unZipSelected(
    treeData: TreeNode[],
    readableSelected: string[]
) {
    return _.reduce(
        readableSelected,
        (memo, val) => {
            if (isCourse(treeData, val)) {
                const children = _.pluck(
                    treeData.find(item => item.value === val)?.children || [],
                    "value"
                );
                return [...memo, ...children];
            }
            return [...memo, val];
        },
        [] as string[]
    );
}

/**
 * 选出不属于任何一个班级的学生列表
 * @param treeData
 * @param students
 * @param courses
 */
export function otherStudents(
    treeData: TreeNode[],
    students: string[],
    courses: string[]
) {
    const selectedTreeData = treeData.filter(item =>
        courses.includes(item.value)
    );

    const data = _.reduce(
        selectedTreeData,
        (memo, course) => {
            const students = _.pluck(course.children || [], "value");
            return [...memo, ...students];
        },
        [] as string[]
    );

    return _.difference(students, data);
}

/**
 * 当前节点是否是课程
 * @param treeData
 * @param node
 */
export function isCourse(treeData: TreeNode[], node: string) {
    return _.some(treeData, { value: node });
}

/**
 * 获取课程下所有子节点
 * @param treeData
 * @param node
 */
export function allChildren(treeData: TreeNode[], node: string): string[] {
    if (!isCourse(treeData, node)) return [];
    const course = _.find(treeData, { value: node });
    return _.pluck(course?.children || [], "value");
}

/**
 * 根据学生id来生成显示用的通知范围字符串
 */
export function makeTargetString(treeData: TreeNode[], studentIds: string[]) {
    const selected = zipSelected(treeData, studentIds);
    const flatTreeData = flattenTreeData(treeData);

    const selectData = flatTreeData.filter(item =>
        selected.includes(item.value)
    );
    return _.pluck(selectData, "title").join(",");
}

//-----------------------------------收到的通知-------------------------------------------
export interface ReceiveListItem {
    readStatus: BooleanValue; //1已读，0未读
    teacherName: string;
    id: number;
    time: string;
    title: string;
    content: string;
}

interface ReceiveList {
    dataList: ReceiveListItem[];
    allRowNum: number;
}

export function getMessageReceiveList(
    pageNo: number,
    pageSize: number,
    onlyNoRead?: number
) {
    const params = qs.stringify({ pageNo, pageSize, onlyNoRead });
    return axios
        .get<SuccessData<ReceiveList>>(
            `/teacher/message/messageReceiveList?${params}`
        )
        .then(res => res.data.data);
}

/**
 * 更新已读状态
 */
export function updateMessageReceiveRead(id: number) {
    return axios.post(
        `/teacher/message/messageReceiveRead`,
        qs.stringify({ id })
    );
}

/**
 * 获取未读数目
 * @returns
 */
export const getNoReadCount = () => {
    return axios
        .get<SuccessData<number>>("/teacher/message/messageReceiveNoReadCount")
        .then(res => res.data.data);
};

/**
 * 全部标记已读
 * */
export const markReadAll = () => {
    return axios.post("/teacher/message/messageReceiveBatchUpdate");
};

/**
 * 清空已读
 * */
export const clearReadAll = () => {
    return axios.post("/teacher/message/messageReceiveBatchDelete");
};
